import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import './App.css';
import { initProtection, cleanupProtection } from './services/antiTamper';
import RootErrorBoundary from './components/RootErrorBoundary';
import OfflineBanner from './components/OfflineBanner';

// Eagerly loaded — required for first paint / auth flow.
import LandingPage from './pages/LandingPage';
import AuthPage from './pages/AuthPage';
import TermsPage from './pages/TermsPage';
import NDAPage from './pages/NDAPage';

// Lazy-loaded — heavy pages (MapLibre, AI vision, etc.) that shouldn't block first paint.
const DashboardPage    = lazy(() => import('./pages/DashboardPage'));
const MapPage          = lazy(() => import('./pages/MapPage'));
const BloodTrackerPage = lazy(() => import('./pages/BloodTrackerPage'));
const PermitsPage      = lazy(() => import('./pages/PermitsPage'));
const PricingPage      = lazy(() => import('./pages/PricingPage'));
const SharedTrailPage  = lazy(() => import('./pages/SharedTrailPage'));
const AdminPage        = lazy(() => import('./pages/AdminPage'));
const TestersGuidePage = lazy(() => import('./pages/TestersGuidePage'));
const LandAccessPage   = lazy(() => import('./pages/LandAccessPage'));
const PromosPage       = lazy(() => import('./pages/PromosPage'));
const ReferralPage     = lazy(() => import('./pages/ReferralPage'));
const BloodTracksPage  = lazy(() => import('./pages/BloodTracksPage'));
const Nouveautes2026Page = lazy(() => import('./pages/Nouveautes2026Page'));

// Context
import { AuthProvider, useAuth } from './context/AuthContext';
import { SpotsProvider } from './context/SpotsContext';

// Components
import { AppShell } from './components/app/AppShell';
import { UpdateBanner } from './components/UpdateBanner';
import { TERMS_VERSION } from './constants/termsContent';

const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#0a0a0a]" data-testid="route-loading">
    <div className="flex flex-col items-center gap-3">
      <div className="w-10 h-10 border-2 border-[#ff6b35] border-t-transparent rounded-full animate-spin" />
      <div className="text-[#ff6b35] text-sm font-mono uppercase tracking-widest">Chargement...</div>
    </div>
  </div>
);

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) return <LoadingFallback />;
  if (!user) return <Navigate to="/login" />;
  if (user.status === 'suspended') return <Navigate to="/login" />;

  if (user.role === 'tester' && !user.nda_accepted && !sessionStorage.getItem('obgh_nda_accepted')) {
    return <Navigate to="/nda" />;
  }

  const termsAccepted = localStorage.getItem('obgh_terms_accepted');
  if (termsAccepted !== TERMS_VERSION) return <Navigate to="/terms" />;

  return children;
};

function App() {
  useEffect(() => {
    initProtection();
    const splash = document.getElementById('obgh-splash');
    if (splash) { splash.classList.add('obgh-splash--out'); setTimeout(() => splash.remove(), 450); }
    return () => cleanupProtection();
  }, []);

  return (
    <RootErrorBoundary>
      <AuthProvider>
        <SpotsProvider>
          <BrowserRouter>
            <div className="App">
              <AppShell>
              <UpdateBanner />
              <Suspense fallback={<LoadingFallback />}>
                <Routes>
                  <Route path="/" element={<LandingPage />} />
                  <Route path="/login" element={<AuthPage />} />
                  <Route path="/register" element={<AuthPage />} />
                  <Route path="/terms" element={<TermsPage />} />
                  <Route path="/nda" element={<NDAPage />} />
                  <Route path="/shared-trail/:token" element={<SharedTrailPage />} />
                  <Route path="/promos" element={<PromosPage />} />
                  <Route path="/nouveautes-2026" element={<Nouveautes2026Page />} />
                  <Route path="/referrals" element={<ReferralPage />} />
                  <Route path="/blood-tracks" element={<BloodTracksPage />} />
                  <Route path="/pricing"       element={<ProtectedRoute><PricingPage /></ProtectedRoute>} />
                  <Route path="/dashboard"     element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
                  <Route path="/map"           element={<ProtectedRoute><MapPage /></ProtectedRoute>} />
                  <Route path="/blood-tracker" element={<ProtectedRoute><BloodTrackerPage /></ProtectedRoute>} />
                  <Route path="/permits"       element={<ProtectedRoute><PermitsPage /></ProtectedRoute>} />
                  <Route path="/admin"         element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
                  <Route path="/testers-guide" element={<ProtectedRoute><TestersGuidePage /></ProtectedRoute>} />
                  <Route path="/land-access"   element={<ProtectedRoute><LandAccessPage /></ProtectedRoute>} />
                </Routes>
              </Suspense>
              </AppShell>
              <OfflineBanner />
              <Toaster position="top-right" richColors />
            </div>
          </BrowserRouter>
        </SpotsProvider>
      </AuthProvider>
    </RootErrorBoundary>
  );
}

export default App;
